import React, { useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import { SafeAreaProvider, initialWindowMetrics } from 'react-native-safe-area-context';
import { NavigationContainer, DefaultTheme, getStateFromPath as defaultGetStateFromPath, getPathFromState as defaultGetPathFromState } from '@react-navigation/native';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';
import { RootNavigator } from './navigation/RootNavigator';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { verifyConfiguration } from './utils/configVerification';
import { EnvironmentBadge } from './components';
import { ActivityIndicator, View, Platform, StatusBar } from 'react-native';
import { ToastProvider } from './contexts/admin/ToastContext';
import { ThemeProvider } from './contexts/residents/ThemeContext';
import { useThemeColors } from './hooks/residents/useThemeColors';
import { AuthAPI } from './services/residents/api/AuthAPI';
import { useImageCacheCleanup } from '@/hooks/admin/useImageCacheCleanup';
import Config from 'react-native-config';
import { PRIMITIVE_COLORS } from './constants/residents/residentColors';

// TODO: update selector to use reusuable `DesktopDropdownSelector` component

// TODO: feedback modal open when clicking on notification
// TODO: mobile see all notifications not working
// TODO: desktop bulletins orange dot position
// TODO: limit to images only in property chat

// TODO: marketplace preview chat not updating

/**
 * Inner component that consumes ThemeContext for dynamic colors.
 * Separated so that useThemeColors() has access to ThemeProvider.
 */
const AppContent: React.FC = () => {
  const { mode, colors } = useThemeColors();

  const navTheme = useMemo(
    () => ({
      ...DefaultTheme,
      dark: mode === 'dark',
      colors: {
        ...DefaultTheme.colors,
        background: colors.bg.base,
      },
    }),
    [mode, colors.bg.base],
  );

  return (
    <>
      <StatusBar barStyle={mode === 'dark' ? 'light-content' : 'dark-content'} />
      <View style={{ flex: 1, backgroundColor: colors.bg.base }}>
        <NavigationContainer
          theme={navTheme}
          documentTitle={{
            formatter: () => 'Resio',
          }}
          linking={{
            prefixes: [
              'https://beta.resio.com',
              'https://alpha.resio.com',
              'https://app.resio.com',
              'https://app.staging.resio.com',
              'http://localhost:5173',
            ],
            config: {
              screens: {
                Auth: {
                  screens: {
                    ResetPassword: 'reset-password',
                    SetPassword: 'set-password',
                    Login: 'login',
                    Signup: 'signup',
                    ForgotPassword: 'forgot-password',
                  },
                } as any,
              },
            },
            // Fallback for URLs managed by the desktop navigator's independent
            // NavigationContainer. Without this, popstate events for paths like
            // /payments return undefined and crash resetRoot.
            getStateFromPath: (path: string, config: any) => {
              const state = defaultGetStateFromPath(path, config);
              if (state) return state;
              // Unrecognised path – stay on Main; the desktop navigator resolves
              // the actual screen from the URL internally.
              return { routes: [{ name: 'Main' as const }] };
            },
            // When the outer container resolves to Main, preserve the current
            // browser URL instead of writing "/Main". The desktop navigator's
            // inner NavigationContainer owns the URL for resident screens.
            getPathFromState: (state: any, config: any) => {
              const win = (globalThis as any).window;
              if (
                state?.routes?.length === 1 &&
                state.routes[0].name === 'Main' &&
                Platform.OS === 'web' &&
                win
              ) {
                return win.location.pathname;
              }
              return defaultGetPathFromState(state, config);
            },
          }}
        >
          <RootNavigator />
        </NavigationContainer>
        <EnvironmentBadge />
      </View>
    </>
  );
};

export const App: React.FC = () => {
  useImageCacheCleanup();

  useEffect(() => {
    initializeApp();
  }, []);

  // Set document title on web to prevent it from being overridden to "undefined"
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.title = 'Resio';
    }
  }, []);

useEffect(() => {
  console.log('Environment Check:', {
    environment: Config.ENVIRONMENT,
    apiUrl: Config.API_BASE_URL,
    timeout: Config.API_TIMEOUT,
    allLoaded: !!(Config.ENVIRONMENT && Config.API_BASE_URL)
  });
}, []);

  const initializeApp = async () => {
    try {
      // Verify configuration
      if (__DEV__) {
        verifyConfiguration();
      }

      // Initialize any services here
      // TODO: Initialize certificate pinning
      // TODO: Initialize analytics

      // Fetch app configuration (matching iOS behavior)
      try {
        await AuthAPI.fetchConfig();
      } catch (error) {
        console.warn('Failed to fetch app configuration:', error);
        // App continues with default config
      }
    } catch (error) {
      console.error('App initialization failed:', error);
    }
  };

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <Provider store={store}>
        <PersistGate
          loading={
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
              <ActivityIndicator size="large" color={PRIMITIVE_COLORS.resioBlue} />
            </View>
          }
          persistor={persistor}
        >
          <ThemeProvider>
            <SafeAreaProvider initialMetrics={initialWindowMetrics}>
              <ToastProvider maxToasts={3}>
                <AppContent />
              </ToastProvider>
            </SafeAreaProvider>
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </GestureHandlerRootView>
  );
};